import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, where, orderBy, doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { TimberKit, KitProposal, createDefaultNodeSet } from '../../types';
import { Package, X, Tag, Check, ChevronRight } from 'lucide-react';
import './KitSelectorModal.css';

interface KitSelectorModalProps {
    isOpen: boolean;
    dealId: string;
    dealTitle: string;
    onClose: () => void;
    onKitAssigned: (kitProposal: KitProposal) => void;
}

const KitSelectorModal: React.FC<KitSelectorModalProps> = ({
    isOpen,
    dealId,
    dealTitle,
    onClose,
    onKitAssigned,
}) => {
    const [kits, setKits] = useState<TimberKit[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedKit, setSelectedKit] = useState<TimberKit | null>(null);
    const [selectedSpecies, setSelectedSpecies] = useState<string>('');
    const [assigning, setAssigning] = useState(false);
    const [step, setStep] = useState<'select-kit' | 'select-species'>('select-kit');

    // Load active kits
    useEffect(() => {
        if (!isOpen) return;

        const kitsRef = collection(db, 'timber_kits');
        const q = query(
            kitsRef,
            where('status', '==', 'active'),
            orderBy('name', 'asc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const kitData: TimberKit[] = [];
            snapshot.forEach((docSnap) => {
                const data = docSnap.data();
                kitData.push({
                    ...data,
                    id: docSnap.id,
                    createdAt: data.createdAt?.toDate?.() || new Date(),
                    updatedAt: data.updatedAt?.toDate?.() || new Date(),
                    saleEndsAt: data.saleEndsAt?.toDate?.() || undefined,
                } as TimberKit);
            });
            setKits(kitData);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [isOpen]);

    // Check if sale is active
    const isSaleActive = (kit: TimberKit) => {
        if (!kit.isOnSale) return false;
        if (kit.saleEndsAt && new Date() > kit.saleEndsAt) return false;
        return true;
    };

    // Get price for selected species or base price
    const getKitPrice = (kit: TimberKit, species?: string): number => {
        if (species && kit.speciesPricing) {
            const sp = kit.speciesPricing.find(s => s.speciesName === species);
            if (sp) return sp.billableTotal;
        }
        return kit.billableTotal;
    };

    // Apply sale discount
    const applyDiscount = (price: number, kit: TimberKit): number => {
        if (!isSaleActive(kit) || !kit.saleDiscount) return price;
        return price * (1 - kit.saleDiscount / 100);
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    const handleSelectKit = (kit: TimberKit) => {
        setSelectedKit(kit);
        // If kit has multiple species, go to species selection
        if (kit.speciesPricing && kit.speciesPricing.length > 1) {
            setStep('select-species');
            setSelectedSpecies(kit.speciesPricing[0].speciesName); // Default to first
        } else if (kit.speciesPricing && kit.speciesPricing.length === 1) {
            // Single species, auto-select
            setSelectedSpecies(kit.speciesPricing[0].speciesName);
            assignKitToDeal(kit, kit.speciesPricing[0].speciesName);
        } else {
            // No species configured
            assignKitToDeal(kit);
        }
    };

    const handleConfirmSpecies = () => {
        if (selectedKit && selectedSpecies) {
            assignKitToDeal(selectedKit, selectedSpecies);
        }
    };

    const assignKitToDeal = async (kit: TimberKit, species?: string) => {
        setAssigning(true);
        try {
            // Generate 4-digit PIN
            const generatePin = () => {
                return Math.floor(1000 + Math.random() * 9000).toString();
            };

            // Create slug
            const slug = `${dealTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${kit.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

            // Create a copy of the kit for this deal (editable proposal)
            const kitProposal: KitProposal = {
                id: dealId,
                kitId: kit.id,
                kitName: kit.name,
                dealId,
                nodeSets: JSON.parse(JSON.stringify(kit.nodeSets)), // Deep copy
                profitMargin: kit.profitMargin,
                selectedSpecies: species,
                designTotal: kit.designTotal,
                timberKitTotal: kit.timberKitTotal,
                installTotal: kit.installTotal,
                rawCostTotal: kit.rawCostTotal,
                billableTotal: getKitPrice(kit, species),
                saleDiscountApplied: isSaleActive(kit) ? kit.saleDiscount : undefined,
                status: 'draft',
                slug,
                accessPin: generatePin(),
                createdAt: new Date(),
                updatedAt: new Date(),
            };

            // Save to kitProposals collection
            const proposalRef = doc(db, 'kitProposals', dealId);
            await setDoc(proposalRef, kitProposal);

            onKitAssigned(kitProposal);
            onClose();
        } catch (error) {
            console.error('Error assigning kit:', error);
            alert('Failed to assign kit. Please try again.');
        } finally {
            setAssigning(false);
        }
    };

    const handleBack = () => {
        setStep('select-kit');
        setSelectedSpecies('');
    };

    if (!isOpen) return null;

    return (
        <div className="kit-selector-overlay" onClick={onClose}>
            <div className="kit-selector-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="kit-selector-header">
                    <div className="header-title">
                        <Package size={22} />
                        <h2>
                            {step === 'select-kit' ? 'Select a Kit' : 'Select Species'}
                        </h2>
                    </div>
                    <button className="close-btn" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="kit-selector-content">
                    {loading ? (
                        <div className="kit-selector-loading">
                            <div className="loading-spinner" />
                            <p>Loading kits...</p>
                        </div>
                    ) : step === 'select-kit' ? (
                        <>
                            {kits.length === 0 ? (
                                <div className="kit-selector-empty">
                                    <Package size={48} strokeWidth={1} />
                                    <h3>No active kits</h3>
                                    <p>Create kits in Kit Management to use them here.</p>
                                </div>
                            ) : (
                                <div className="kit-list">
                                    {kits.map((kit) => {
                                        const price = kit.billableTotal;
                                        const saleActive = isSaleActive(kit);
                                        const discountedPrice = saleActive
                                            ? applyDiscount(price, kit)
                                            : price;

                                        return (
                                            <div
                                                key={kit.id}
                                                className={`kit-option ${saleActive ? 'on-sale' : ''}`}
                                                onClick={() => handleSelectKit(kit)}
                                            >
                                                {/* Thumbnail */}
                                                <div className="kit-option-thumbnail">
                                                    {kit.thumbnailUrl ? (
                                                        <img src={kit.thumbnailUrl} alt={kit.name} />
                                                    ) : (
                                                        <Package size={32} strokeWidth={1} />
                                                    )}
                                                </div>

                                                {/* Info */}
                                                <div className="kit-option-info">
                                                    <h4>{kit.name}</h4>
                                                    {kit.description && (
                                                        <p className="kit-option-desc">{kit.description}</p>
                                                    )}
                                                    {kit.speciesPricing && kit.speciesPricing.length > 1 && (
                                                        <span className="species-count">
                                                            {kit.speciesPricing.length} species available
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Pricing */}
                                                <div className="kit-option-price">
                                                    {saleActive && (
                                                        <>
                                                            <span className="original-price">
                                                                {formatCurrency(price)}
                                                            </span>
                                                            <span className="sale-badge">
                                                                <Tag size={12} />
                                                                {kit.saleDiscount}% OFF
                                                            </span>
                                                        </>
                                                    )}
                                                    <span className="final-price">
                                                        {formatCurrency(discountedPrice)}
                                                    </span>
                                                </div>

                                                <ChevronRight size={20} className="kit-option-arrow" />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    ) : (
                        /* Species selection step */
                        <div className="species-selection">
                            <button className="back-btn" onClick={handleBack}>
                                ← Back to kits
                            </button>

                            {selectedKit && (
                                <>
                                    <div className="selected-kit-info">
                                        <h3>{selectedKit.name}</h3>
                                        {isSaleActive(selectedKit) && (
                                            <span className="sale-badge">
                                                <Tag size={12} />
                                                {selectedKit.saleDiscount}% OFF
                                            </span>
                                        )}
                                    </div>

                                    <p className="species-prompt">Choose a wood species:</p>

                                    <div className="species-list">
                                        {selectedKit.speciesPricing?.map((sp) => {
                                            const price = sp.billableTotal;
                                            const saleActive = isSaleActive(selectedKit);
                                            const discountedPrice = saleActive
                                                ? price * (1 - (selectedKit.saleDiscount || 0) / 100)
                                                : price;

                                            return (
                                                <div
                                                    key={sp.speciesName}
                                                    className={`species-option ${selectedSpecies === sp.speciesName ? 'selected' : ''}`}
                                                    onClick={() => setSelectedSpecies(sp.speciesName)}
                                                >
                                                    <span className="species-name">{sp.speciesName}</span>
                                                    <div className="species-price">
                                                        {saleActive && (
                                                            <span className="original-price">
                                                                {formatCurrency(price)}
                                                            </span>
                                                        )}
                                                        <span className="final-price">
                                                            {formatCurrency(discountedPrice)}
                                                        </span>
                                                    </div>
                                                    {selectedSpecies === sp.speciesName && (
                                                        <Check size={18} className="check-icon" />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                {step === 'select-species' && selectedSpecies && (
                    <div className="kit-selector-footer">
                        <button
                            className="confirm-btn"
                            onClick={handleConfirmSpecies}
                            disabled={assigning}
                        >
                            {assigning ? 'Assigning...' : 'Assign Kit to Deal'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default KitSelectorModal;
